import React, { useRef, useState, useEffect } from 'react';
import { Grid, TextField, Box, Button, Select, useTheme, Typography, ThemeProvider, MenuItem } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useNavigate } from 'react-router-dom';
import getTheme from '../../theme/theme';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Backdrop, CircularProgress, } from "@mui/material";
import { BASEURL, ARROW, STATUS, AUTOFILL_STYLES } from "../../constants/Common"
import FloatingButton from '../usersupport/helpdesk/FloatingButton';

export interface ReportInputData {
    id: number;
    title: string;
    description: string;
    createdby: string;
    createddate: string;
    featurenames: string[];
    columns: string[];
    featurecreatedby: string[];
    status: string[];
    start_date: string;
    end_date: string;
    project_id: string;
    project_name: string;

}

export interface ReportDetails {
    id: number;
    projectcode: string;
    title: string;
    status: string;
    assignedto: string;
    description: string;
    createdby: string;
    createddate: string;
    featurename: string;
    projectname: string;

}

const CreateReport = () => {
    const navigate = useNavigate();
    const selectedProjectId = useSelector((state: RootState) => state.project.selectedProjectTitle);
    const [isLoading, setIsLoading] = useState(false);
    const theme = useTheme();

    const today = new Date().toISOString().slice(0, 10);
    const orgid = localStorage.getItem('orgid');
    const username = localStorage.getItem('username');
    const mergedListRef = useRef(0);

    const column_list = ['Select All', 'Title', 'Created By', 'Created On', 'Status']
    const [createdbyList, setCreatedbyList] = useState<string[]>(["Select All"]);
    const [featurename_list, setfeaturename_list] = useState<string[]>(["Select All"]);
    const allowed_feature = ["Daily Logs", "Work Order", "Service Tickets", "Punch Lists", "Todo Lists", "Safety Logs","Vehicle Logs", "Inspections"]

    const [selReport, setSelReport] = useState<ReportInputData>({
        id: 0,
        title: '',
        description: '',
        createdby: username ? username : '',
        createddate: today,
        featurenames: [featurename_list[0]],
        columns: [column_list[0]],
        featurecreatedby: [createdbyList[0]],
        status: [STATUS[0]],
        start_date: '',
        end_date: '',
        project_id: '',
        project_name: '',
    })


    useEffect(() => {
        //First time update allowed feature names and fetch usernames from db
        if (mergedListRef.current === 0) {  //use this condition to fetch and populate list only one time
            const mergedList = [...featurename_list, ...allowed_feature];
            setfeaturename_list(mergedList);
            fetchUserNamelist();
            mergedListRef.current = 1
        }

        // check user logged in or session expired, then navigate to login
        if (!orgid)
            navigate('/login')

    }, []);

    const fetchUserNamelist = async () => {
        //To fetch usernames from user table for populating createdby field   
        try {
            const userlist_url = `${BASEURL}/users/userlist/?orgid=${orgid}`
            const response = await axios.get(userlist_url, {
                headers: { Accept: 'application/json', },
            });
            if (response.status === 200) {
                const userNames_list = response.data.user_names;
                const names = [];
                names.push(...userNames_list.map((user: any) => user.username));
                const mergedList = [...createdbyList, ...names];
                setCreatedbyList(mergedList);
            }

        } catch (error) {
            console.log(error);
        }
    }
    const fetchReportDetails = async () => {
        //To fetch report details of the all features - title, description, status, createdby, createddate   
        try {

            setIsLoading(true)
            let sel_features = selReport.featurenames
            if (sel_features[0] === 'Select All')
                sel_features = allowed_feature


            if (selReport.columns[0] === 'Select All') selReport.columns = ['Title', 'Created By', 'Created On', 'Status'];
            const allColumns = ['Feature Name', 'Project Name', ...selReport.columns];
            selReport.columns = allColumns;

            const repdetails_url = `${BASEURL}/getreportdetails/?orgid=${orgid}&projectid=${selectedProjectId}
                &feature_names=${sel_features}&createdby=${selReport.featurecreatedby}&status=${selReport.status}
                &createdon_start=${selReport.start_date}&createdon_end=${selReport.end_date}`;
            const response = await axios.get(repdetails_url, {
                headers: { Accept: 'application/json', },
            });

            if (response.status === 202)
                toast.error("No data found! Please change filter and retry.")
            else if (response.status === 200) {
                const report_dat = response.data.data;
                const projName = report_dat[0]?.projectname;
                if (selectedProjectId === '' || selectedProjectId === '0')
                    selReport.project_name = 'All'
                else
                    selReport.project_name = projName

                //             setReportDetails(report_dat)
                navigate('/viewreport', { state: { report_input: selReport, report_details: report_dat } });
            }
            else
                toast.error("Error in creating Report, please check with helpdesk.")
        } catch (error) {
            console.log(error);
        }
        finally {
            setIsLoading(false)
        }
    };

    const validateInput = () => {
        //Validate the mandatory fields entered or not
        if (selReport?.title.length === 0) {
            toast.error("Please enter Report title.")
            return false;
        }
        if (selReport?.description.length === 0) {
            toast.error("Please enter Report description.")
            return false;
        }
        return true;
    }

    const handleGenerateReport = () => {

        const ret = validateInput();
        if (!ret)
            return
        fetchReportDetails();
    }

    // Function to remove 'select all' from a comma-separated string
    const removeSelectAll = (value: string[]): string[] => {
        return value.filter(item => item !== 'Select All');
    };


    const handleFormChange = (event: any) => {
        const { name, value } = event.target;
        let updatedValue = value;

        if (selReport) {
            if (name === 'createdby' || 'featurename' || 'columns' || 'status')
                if (value.length > 1)
                    if (value[0] === 'Select All')
                        updatedValue = removeSelectAll(value);
                    else if (value[value.length - 1] === 'Select All')
                        updatedValue = ['Select All'];

            setSelReport((prevData) => ({
                ...prevData!,
                [name]: updatedValue,
            }));

        }
    };


    return (
        <ThemeProvider theme={getTheme()}>
            <Backdrop
                sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={isLoading}
            >
                <CircularProgress color='inherit' />
            </Backdrop>

            <Box sx={{ position: 'relative', top: 0, right: 0, mt: '-70px' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant='h6' gutterBottom>
                        Reports {ARROW} Create Custom Report
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Button variant='contained' color='primary'
                            onClick={handleGenerateReport}
                            sx={{ mr: 1 }}
                        >
                            Generate Report
                        </Button>
                    </Box>
                </Box>

                <Typography variant="body1" sx={{ mt: 2, color: 'grey.500', fontStyle: 'italic' }}>
                    Choose a project or 'All' from the dropdown menu in the header before generating the report.
                </Typography>

                <Grid container spacing={2} sx={{ mt: '30px' }}>
                    <Grid item xs={12}>
                        <Box>
                            <Grid container spacing={2}>

                                <Grid item xs={3} sx={{ mt: '0px', ml: '30px' }}>
                                    <p><strong>Report Title</strong></p>
                                </Grid>
                                <Grid item xs={3} sx={{ mb: '-10px', ml: '-30px', ...AUTOFILL_STYLES(theme) }}>
                                    <TextField variant='outlined' name='title'
                                        value={selReport?.title} onChange={handleFormChange} fullWidth
                                    />
                                </Grid>
                                <Grid item xs={3} sx={{ mt: '0px', ml: '30px' }}>
                                    <p><strong>Created By</strong></p>
                                </Grid>

                                <Grid item xs={3} sx={{ mt: '10px', mb: '0px', ml: '-30px' }}>
                                    {selReport.createdby}
                                </Grid>

                                <Grid item xs={3} sx={{ mt: '0px', ml: '30px' }}>
                                    <p><strong>Description</strong></p>
                                </Grid>
                                <Grid item xs={3} sx={{ mb: '-10px', ml: '-30px' }}>
                                    <TextField
                                        variant='outlined'
                                        name='description'
                                        value={selReport?.description} multiline={true} rows={3}
                                        onChange={handleFormChange}
                                        fullWidth
                                    />
                                </Grid>

                                <Grid item xs={3} sx={{ mt: '0px', ml: '30px' }}>
                                    <p><strong>Created Date</strong></p>
                                </Grid>
                                <Grid item xs={3} sx={{ mt: '15px', mb: '0px', ml: '-30px' }}>
                                    {today}
                                </Grid>


                                <Grid container spacing={2} sx={{
                                    ml: '20px', mt: '30px', mb: '10px', border: '1px solid #ccc', borderRadius: 4,
                                    paddingBottom: '10px', paddingTop: '0px'
                                }}>

                                    <Grid item xs={3} sx={{ mt: '20px', ml: '10px' }}>
                                        <p><strong>Select Feature</strong></p>
                                    </Grid>

                                    <Grid item xs={3} sx={{ mt: '20px', ml: '-30px' }}>
                                        <Select
                                            value={selReport.featurenames} name="featurenames"
                                            onChange={(event) => handleFormChange(event)}
                                            fullWidth variant="outlined" multiple
                                            sx={{ width: "100%", maxWidth: '350px', height: 40, }}
                                        >
                                            {featurename_list.map((type: string) => (
                                                <MenuItem key={type} value={type}>
                                                    {type}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Grid>

                                    <Grid item xs={3} sx={{ mt: '20px', ml: '30px' }}>
                                        <p><strong>Select Column</strong></p>
                                    </Grid>

                                    <Grid item xs={3} sx={{ mt: '20px', ml: '-30px' }}>
                                        <Select
                                            value={selReport.columns} name="columns"
                                            onChange={(event) => handleFormChange(event)}
                                            fullWidth variant="outlined" multiple
                                            sx={{ width: "100%", maxWidth: '350px', height: 40, }}
                                        >
                                            {column_list.map((type: string) => (
                                                <MenuItem key={type} value={type}>
                                                    {type}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Grid>

                                    <Grid item xs={3} sx={{ mt: '0px', ml: '10px' }}>
                                        <p><strong>Created By</strong></p>
                                    </Grid>

                                    <Grid item xs={3} sx={{ mt: '0px', ml: '-30px' }}>
                                        <Select
                                            value={selReport.featurecreatedby} multiple
                                            onChange={(event) => handleFormChange(event)}
                                            fullWidth variant="outlined" name="featurecreatedby"
                                            sx={{ width: "100%", maxWidth: '350px', height: 40, }}
                                        >
                                            {createdbyList.map((type: string) => (
                                                <MenuItem key={type} value={type}>
                                                    {type}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Grid>

                                    <Grid item xs={3} sx={{ mt: '0px', ml: '30px' }}>
                                        <p><strong> Status</strong></p>
                                    </Grid>

                                    <Grid item xs={3} sx={{ mt: '0px', ml: '-30px' }}>
                                        <Select
                                            value={selReport.status} multiple
                                            onChange={(event) => handleFormChange(event)}
                                            fullWidth variant="outlined" name="status"
                                            sx={{ width: "100%", maxWidth: '350px', height: 40, }}
                                        >
                                            {STATUS.map((type: string) => (
                                                <MenuItem key={type} value={type}>
                                                    {type}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Grid>

                                    <Grid item xs={3} sx={{ mt: '0px', ml: '10px' }}>
                                        <p><strong>Created From</strong></p>
                                    </Grid>

                                    <Grid item xs={3} sx={{ mt: '0px', ml: '-30px' }}>
                                        <TextField defaultValue={selReport?.start_date} type="date" onChange={(event) => handleFormChange(event)}
                                            fullWidth variant="outlined" name="start_date"
                                        />
                                    </Grid>

                                    <Grid item xs={3} sx={{ mt: '0px', ml: '30px' }}>
                                        <p><strong>Created Till</strong></p>
                                    </Grid>
                                    <Grid item xs={3} sx={{ mt: '0px', ml: '-30px' }}>
                                        <TextField defaultValue={selReport?.end_date} type="date" onChange={(event) => handleFormChange(event)}
                                            fullWidth variant="outlined" name="end_date" />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>
                </Grid>
                <FloatingButton/>
            </Box>

        </ThemeProvider>
    )
}
export default CreateReport;

